// Small polygon helpers for angled property lines / buildable envelopes.

export function polygonCentroid(pts) {
  let x = 0, y = 0;
  pts.forEach((p) => { x += p.x; y += p.y; });
  return { x: x / pts.length, y: y / pts.length };
}

export function polygonArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
}

function lineIntersect(l1, l2) {
  const det = l1.dx * -l2.dy - l1.dy * -l2.dx;
  if (Math.abs(det) < 1e-9) return null;
  const rx = l2.px - l1.px, ry = l2.py - l1.py;
  const t = (rx * -l2.dy - ry * -l2.dx) / det;
  return { x: l1.px + l1.dx * t, y: l1.py + l1.dy * t };
}

// Inset a simple polygon inward by d feet (approximate; good for convex/mild-concave lots).
export function insetPolygon(pts, d) {
  if (!pts || pts.length < 3 || !d || d <= 0) return pts;
  const c = polygonCentroid(pts);
  const n = pts.length;
  const lines = [];
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    let dx = b.x - a.x, dy = b.y - a.y;
    const L = Math.hypot(dx, dy) || 1; dx /= L; dy /= L;
    let nx = -dy, ny = dx;
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    if ((c.x - mx) * nx + (c.y - my) * ny < 0) { nx = -nx; ny = -ny; }
    lines.push({ px: a.x + nx * d, py: a.y + ny * d, dx, dy });
  }
  const out = [];
  for (let i = 0; i < n; i++) {
    const p = lineIntersect(lines[(i - 1 + n) % n], lines[i]);
    out.push(p ? { x: +p.x.toFixed(2), y: +p.y.toFixed(2) } : { ...pts[i] });
  }
  return out;
}

export function pointsAttr(pts) {
  return (pts || []).map((p) => `${p.x},${p.y}`).join(" ");
}

// Rectangle → polygon points
export function rectToPolygon(r) {
  return [
    { x: r.x, y: r.y }, { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h },
  ];
}

// ---- Metes & Bounds (survey courses) ----
// Parse a survey call like: N29°42'41"W - 124.00'  /  N 29-42-41 W 124  /  s 5 30 0 e 88.5
export function parseCourse(raw) {
  if (!raw) return null;
  const s = String(raw).toUpperCase();
  const nsM = s.match(/[NS]/);
  if (!nsM) return null;
  const ns = s[nsM.index];
  const rest = s.slice(nsM.index + 1);
  const ewM = rest.match(/[EW]/);
  if (!ewM) return null;
  const ew = rest[ewM.index];
  const bnums = (rest.slice(0, ewM.index).match(/\d+(?:\.\d+)?/g) || []).map(Number);
  const [deg = 0, min = 0, sec = 0] = bnums;
  const dnums = rest.slice(ewM.index + 1).match(/\d+(?:\.\d+)?/g) || [];
  const dist = dnums.length ? Number(dnums[0]) : 0;
  return { ns, deg, min, sec, ew, dist };
}

// Nicely formatted label for a course
export function courseLabel(c) {
  const d = Math.floor(Number(c.deg) || 0);
  const m = Math.floor(Number(c.min) || 0);
  const sec = Math.round(Number(c.sec) || 0);
  return `${c.ns} ${d}°${String(m).padStart(2, "0")}'${String(sec).padStart(2, "0")}" ${c.ew} — ${(Number(c.dist) || 0).toFixed(2)}'`;
}

// Screen-space delta for a course (North = up, East = right; SVG y grows downward)
export function courseDelta(c) {
  const dec = (Number(c.deg) || 0) + (Number(c.min) || 0) / 60 + (Number(c.sec) || 0) / 3600;
  const rad = (dec * Math.PI) / 180;
  const east = (c.ew === "E" ? 1 : -1) * Math.sin(rad);
  const north = (c.ns === "N" ? 1 : -1) * Math.cos(rad);
  const dist = Number(c.dist) || 0;
  return { dx: east * dist, dy: -north * dist };
}

// Build a polygon from a Point of Beginning and an ordered list of courses
export function metesToPolygon(pob, courses) {
  if (!pob || !courses || !courses.length) return [];
  const pts = [{ x: +pob.x.toFixed(2), y: +pob.y.toFixed(2) }];
  let cur = { x: pob.x, y: pob.y };
  courses.forEach((c) => {
    const d = courseDelta(c);
    cur = { x: cur.x + d.dx, y: cur.y + d.dy };
    pts.push({ x: +cur.x.toFixed(2), y: +cur.y.toFixed(2) });
  });
  return pts;
}

// Andrew's monotone-chain convex hull (for building shadow sweeps)
export function convexHull(points) {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = [];
  for (const p of pts) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop(); lower.push(p); }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) { const p = pts[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop(); upper.push(p); }
  lower.pop(); upper.pop();
  return lower.concat(upper);
}
